import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';
import VideoPlayer, { ToCanvasFn } from './VideoPlayer';
import { CurvePoints, drawCurveOverlay } from './curveOverlay';

interface VideoInfo {
    schedule_id: number;
    video_id: number;
    video_url: string;
    parameter: any;
    period_order: number;
    sport_type: string;
    is_undistorted: boolean;
    curve_points: CurvePoints | null;
    fps: number;
}

const CurveDatasetBuilder: React.FC = () => {
    const [scheduleIdsInput, setScheduleIdsInput] = useState<string>('');
    const [scheduleIds, setScheduleIds] = useState<number[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [extracting, setExtracting] = useState<boolean>(false);
    const [extractResult, setExtractResult] = useState<{ success: boolean; message: string } | null>(null);
    const [currentFrame, setCurrentFrame] = useState<number>(0);
    const [loadingDataset, setLoadingDataset] = useState<boolean>(false);
    const [jumpInput, setJumpInput] = useState<string>('');
    const [deleting, setDeleting] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [massExtracting, setMassExtracting] = useState<boolean>(false);
    const [sports, setSports] = useState<string[]>([]);
    const [selectedSport, setSelectedSport] = useState<string>('');

    useEffect(() => {
        const fetchSports = async () => {
            try {
                const response = await apiFetch('/train/sports');
                if (response.ok) {
                    const data = await response.json();
                    setSports(data.sports || []);
                }
            } catch (err) {
                console.error('Failed to load sports:', err);
            }
        };
        fetchSports();
    }, []);

    const loadDatasetQuery = async (sportType?: string) => {
        setLoadingDataset(true);
        setError(null);
        try {
            let url = '/train/curve_dataset_schedules?max_count=30000';
            if (sportType) {
                url += `&sport_type=${encodeURIComponent(sportType)}`;
            }
            const response = await apiFetch(url);
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || `HTTP ${response.status}`);
            }
            const data = await response.json();
            const ids = data.schedule_ids as number[];
            setScheduleIdsInput(ids.join(', '));
            setScheduleIds(ids);
            setCurrentIndex(0);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoadingDataset(false);
        }
    };

    const loadScheduleIds = () => {
        const ids = scheduleIdsInput
            .split(',')
            .map(s => parseInt(s.trim()))
            .filter(n => !isNaN(n));
        if (ids.length > 0) {
            setScheduleIds(ids);
            setCurrentIndex(0);
        }
    };

    const fetchVideoInfo = async (scheduleId: number) => {
        setLoading(true);
        setError(null);
        setExtractResult(null);
        try {
            const response = await apiFetch(`/train/video_info?schedule_id=${scheduleId}`);
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || `HTTP ${response.status}`);
            }
            const data = await response.json();
            setVideoInfo(data);
        } catch (err: any) {
            setError(err.message);
            setVideoInfo(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (scheduleIds.length > 0 && currentIndex < scheduleIds.length) {
            fetchVideoInfo(scheduleIds[currentIndex]);
        }
    }, [scheduleIds, currentIndex]);

    const handleExtractFrame = async () => {
        if (!videoInfo) return;

        const frameNumber = currentFrame;

        setExtracting(true);
        setExtractResult(null);

        try {
            const response = await apiFetch('/train/extract_curve_frame', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    schedule_id: videoInfo.schedule_id,
                    frame_number: frameNumber,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || `HTTP ${response.status}`);
            }

            setExtractResult({
                success: true,
                message: `Extracted: ${data.dataset_name} (frame ${frameNumber})`,
            });
        } catch (err: any) {
            setExtractResult({
                success: false,
                message: `Error: ${err.message}`,
            });
        } finally {
            setExtracting(false);
        }
    };

    const handleDeleteScheduleData = async () => {
        if (!videoInfo) return;
        
        const confirmed = window.confirm(
            `Delete ALL data for schedule ${videoInfo.schedule_id}?\n\nThis will remove all extracted frames and curves from BigQuery and GCS.`
        );
        if (!confirmed) return;

        setDeleting(true);
        try {
            const response = await apiFetch(`/train/delete_schedule_data?schedule_id=${videoInfo.schedule_id}`, {
                method: 'DELETE',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || `HTTP ${response.status}`);
            }
            setExtractResult({
                success: true,
                message: `Deleted ${data.deleted_count} rows for schedule ${videoInfo.schedule_id}`,
            });
        } catch (err: any) {
            setExtractResult({
                success: false,
                message: `Delete error: ${err.message}`,
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleRefresh = async () => {
        if (!videoInfo) return;
        setRefreshing(true);
        setError(null);
        try {
            const response = await apiFetch(`/train/refresh_schedule_data?schedule_id=${videoInfo.schedule_id}`, {
                method: 'POST',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || `HTTP ${response.status}`);
            }
            setVideoInfo(data);
            setExtractResult({ success: true, message: 'Cache refreshed' });
        } catch (err: any) {
            setError(err.message);
        } finally {
            setRefreshing(false);
        }
    };

    const handleMassExtract = async () => {
        if (!videoInfo) return;
        
        const targetCount = window.prompt('Enter target frame count:', '50');
        if (!targetCount) return;
        
        const count = parseInt(targetCount, 10);
        if (isNaN(count) || count <= 0) {
            setExtractResult({ success: false, message: 'Invalid target count' });
            return;
        }

        setMassExtracting(true);
        setExtractResult(null);
        try {
            const response = await apiFetch('/train/mass_extract_curves', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    schedule_id: videoInfo.schedule_id,
                    target_count: count,
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || `HTTP ${response.status}`);
            }
            setExtractResult({
                success: true,
                message: `Mass extraction started (${count} frames)`,
            });
        } catch (err: any) {
            setExtractResult({
                success: false,
                message: `Error: ${err.message}`,
            });
        } finally {
            setMassExtracting(false);
        }
    };

    const drawOverlay = useCallback((ctx: CanvasRenderingContext2D, toCanvas: ToCanvasFn) => {
        drawCurveOverlay(ctx, toCanvas, videoInfo?.curve_points ?? null, videoInfo?.parameter?.inputPoints);
    }, [videoInfo]);

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < scheduleIds.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleJump = () => {
        const idx = parseInt(jumpInput, 10);
        if (!isNaN(idx) && idx >= 1 && idx <= scheduleIds.length) {
            setCurrentIndex(idx - 1);
            setJumpInput('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.target instanceof HTMLInputElement) return;
        if (e.key === 'ArrowLeft') handlePrev();
        if (e.key === 'ArrowRight') handleNext();
    };

    return (
        <div 
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            onKeyDown={handleKeyDown}
            tabIndex={0}
        >
            {/* Input Section */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Enter schedule IDs (comma-separated)"
                    value={scheduleIdsInput}
                    onChange={(e) => setScheduleIdsInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadScheduleIds()}
                    style={{ flex: 1, padding: '8px' }}
                />
                <button onClick={loadScheduleIds} style={{ padding: '8px 16px' }}>Load</button>
                
                <select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    style={{ padding: '8px' }}
                >
                    <option value="">All Sports</option>
                    {sports.map(sport => (
                        <option key={sport} value={sport}>{sport}</option>
                    ))}
                </select>
                <button 
                    onClick={() => loadDatasetQuery(selectedSport || undefined)} 
                    disabled={loadingDataset}
                    style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white', border: 'none' }}
                >
                    {loadingDataset ? 'Loading...' : 'Load Dataset'}
                </button>
            </div>

            {/* Navigation + Extract */}
            {scheduleIds.length > 0 && (
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={handlePrev} disabled={currentIndex === 0}>← Prev</button>
                    <span>
                        Schedule{' '}
                        <input
                            type="text"
                            value={jumpInput || (currentIndex + 1)}
                            onChange={(e) => setJumpInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                            onBlur={handleJump}
                            style={{ width: '60px', textAlign: 'center', padding: '2px 4px' }}
                        />
                        {' '}/ {scheduleIds.length}: 
                        <strong style={{ marginLeft: '5px' }}>{scheduleIds[currentIndex]}</strong>
                    </span>
                    <button onClick={handleNext} disabled={currentIndex === scheduleIds.length - 1}>Next →</button>
                    
                    {videoInfo && (
                        <>
                            <span style={{ marginLeft: '20px', color: '#666' }}>
                                Video: {videoInfo.video_id} | 
                                {videoInfo.sport_type} | 
                                Frame: {currentFrame}
                                {videoInfo.curve_points && ' | Curves ✓'}
                            </span>
                            
                            <button 
                                onClick={handleExtractFrame}
                                disabled={extracting || !videoInfo.curve_points}
                                style={{ 
                                    marginLeft: 'auto',
                                    padding: '8px 16px',
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    cursor: extracting ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {extracting ? 'Extracting...' : 'Extract Frame'}
                            </button>

                            <button 
                                onClick={handleMassExtract}
                                disabled={massExtracting || !videoInfo.curve_points}
                                style={{ 
                                    marginLeft: '10px',
                                    padding: '8px 16px',
                                    backgroundColor: '#9C27B0',
                                    color: 'white',
                                    border: 'none',
                                    cursor: massExtracting ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {massExtracting ? 'Starting...' : 'Mass Extract'}
                            </button>

                            <button 
                                onClick={handleDeleteScheduleData}
                                disabled={deleting}
                                style={{ 
                                    marginLeft: '10px',
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    cursor: deleting ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {deleting ? 'Deleting...' : 'Delete Schedule Data'}
                            </button>

                            <button 
                                onClick={handleRefresh}
                                disabled={refreshing}
                                style={{ 
                                    marginLeft: '10px',
                                    padding: '8px 16px',
                                    backgroundColor: '#17a2b8',
                                    color: 'white',
                                    border: 'none',
                                    cursor: refreshing ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {refreshing ? 'Refreshing...' : 'Refresh'}
                            </button>
                            
                            {/* Result message - inline to avoid layout shift */}
                            {extractResult && (
                                <span style={{ 
                                    padding: '4px 12px', 
                                    backgroundColor: extractResult.success ? '#d4edda' : '#f8d7da',
                                    color: extractResult.success ? '#155724' : '#721c24',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    marginLeft: '10px',
                                }}>
                                    {extractResult.message}
                                </span>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Status */}
            {loading && <div>Loading video info...</div>}
            {error && <div style={{ color: 'red' }}>Error: {error}</div>}

            {/* Video Player with Curve Overlay */}
            {videoInfo && !loading && (
                <VideoPlayer
                    videoUrl={videoInfo.video_url}
                    fps={videoInfo.fps}
                    onFrameChange={setCurrentFrame}
                    drawOverlay={drawOverlay}
                />
            )}

            {/* Empty State */}
            {!videoInfo && !loading && !error && scheduleIds.length === 0 && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                    Enter schedule IDs above and click Load to start
                </div>
            )}
        </div>
    );
};

export default CurveDatasetBuilder;